import React, { useEffect, useState } from 'react';
import StepShell from '../StepShell';
import { apiHandler } from '../../../../../api/apihandler';
import { PEOPLE_YOU_MAY_KNOW, FOLLOW_UNFOLLOW } from '../../../../../api/constants';
import { apiSucceeded } from '../apiUtils';

const AVATAR_COLORS = ['#E38A6A', '#82C8B5', '#B7ACEA', '#EFC26C', '#6ABDE3', '#C8A2C8'];

const FollowStep = ({ pendingToken, onSubmit, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [inProgress, setInProgress] = useState(new Set());

  const authHeaders = pendingToken ? { 'x-auth-token': pendingToken } : {};

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      const res = await apiHandler(PEOPLE_YOU_MAY_KNOW, 'POST', { page: 1, limit: 20 }, authHeaders);
      if (!isMounted) return;
      if (apiSucceeded(res)) {
        setUsers(res?.data?.data?.users?.data || []);
      } else {
        setError('Unable to load suggested cooks right now.');
      }
      setLoading(false);
    };

    load();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFollow = async (userId, index, isCurrentlyFollowing) => {
    if (inProgress.has(userId)) return;
    setInProgress((prev) => new Set([...prev, userId]));

    const res = await apiHandler(FOLLOW_UNFOLLOW, 'POST', { follower_id: userId }, authHeaders);
    if (apiSucceeded(res)) {
      setUsers((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], is_following: isCurrentlyFollowing ? 0 : 1 };
        return copy;
      });
    }

    setInProgress((prev) => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  };

  const followAll = async () => {
    const unfollowed = users.filter((u) => u.is_following !== 1);
    for (const user of unfollowed) {
      const index = users.indexOf(user);
      // eslint-disable-next-line no-await-in-loop
      await toggleFollow(user.id, index, false);
    }
  };

  const followedCount = users.filter((u) => u.is_following === 1).length;

  return (
    <StepShell
      step={5}
      title="Follow your first cooks"
      subtitle="Your feed comes alive when you follow people who inspire you"
      onBack={onBack}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
        <div className="ob-follow-head">
          <div className="ob-section-label" style={{ margin: 0 }}>People you may know</div>
          <button type="button" className="ob-follow-all" onClick={followAll} disabled={loading}>Follow all</button>
        </div>

        {loading && <p className="ob-subtitle">Loading suggestions…</p>}
        {error && <p className="ob-error-text">{error}</p>}

        {!loading && !error && users.length === 0 && (
          <p className="ob-subtitle">No suggestions right now.</p>
        )}

        <div className="ob-follow-list">
          {users.map((user, index) => {
            const isFollowing = user.is_following === 1;
            const initial = (user.first_name || '?')[0].toUpperCase();
            const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
            return (
              <div key={String(user.id)} className="ob-follow-card">
                {user.user_setting?.photo ? (
                  <div
                    className="ob-follow-avatar"
                    style={{ backgroundImage: `url(${user.user_setting.photo})`, backgroundSize: 'cover' }}
                  />
                ) : (
                  <div className="ob-follow-avatar" style={{ background: color }}>{initial}</div>
                )}
                <div className="ob-follow-info">
                  <div className="ob-follow-name">{user.first_name} {user.last_name}</div>
                  <div className="ob-follow-meta">{user.follower_count ?? 0} followers</div>
                </div>
                <button
                  type="button"
                  className={`ob-follow-btn${isFollowing ? ' following' : ''}`}
                  disabled={inProgress.has(user.id)}
                  onClick={() => toggleFollow(user.id, index, isFollowing)}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="ob-spacer" />

        <button type="button" className="ob-btn-primary" onClick={onSubmit}>
          Start cooking with {followedCount} follow{followedCount === 1 ? '' : 's'} →
        </button>
        <button type="button" className="ob-skip-link" onClick={onSubmit}>Skip for now →</button>
      </div>
    </StepShell>
  );
};

export default FollowStep;
